import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import LegacyCharts from '../../../../../ProfitCalculations/Content/SingleProfitCalculation/Result/Charts';
import { ChartType } from '../../../../../ProfitCalculations/types';
import { useResultData } from '../../context';
import { ComponentDimensions } from '../../../../../../types/Layout';
import { ChartModeHistories } from '../../derived/chartMode';

export interface TransactionTypeToggleValues {
  core: boolean;
  takeProfit: boolean;
  stopLoss: boolean;
  /** Subscription-only dot types (CPC distributions, balance deposits and
   * withdrawals). Undefined reads as on. */
  distribution?: boolean;
  balance?: boolean;
}

interface Props {
  height: number;
  selectedChartTypes: ChartType[];
  onSelectedChartTypesChange: (next: ChartType[]) => void;
  /** Draw the single outperformance line instead of the selected series. */
  showOutperformance?: boolean;
  /**
   * The series as drawn. Passed in rather than derived here so the x-axis footer and the
   * chart read the same numbers — deriving it on both sides lets them drift apart silently.
   */
  histories?: ChartModeHistories;
  transactionTypeToggleValues: TransactionTypeToggleValues;
  onTransactionTypeToggleValuesChange: (
    next: TransactionTypeToggleValues,
  ) => void;
  onLeftAxisWidthChange?: (width: number) => void;
  onTimeAxisChange?: (info: {
    paddingLeft: number;
    paddingRight: number;
    domainStart: number;
    domainEnd: number;
  }) => void;
}

const noop = () => undefined;

/**
 * The balance/price history maps span the full backtest (often tens of
 * thousands of points). Victory renders every point, so feeding the raw
 * history makes each re-render extremely slow. Evenly sample down to a
 * resolution that is still visually lossless for an on-screen chart.
 */
const downsampleHistory = <T extends Record<string, number> | undefined>(
  record: T,
  max = 800,
): T => {
  if (!record) return record;
  const entries = Object.entries(record);
  if (entries.length <= max) return record;
  const step = Math.ceil(entries.length / max);
  const sampled = entries.filter((_, i) => i % step === 0);
  const last = entries[entries.length - 1];
  if (sampled[sampled.length - 1]?.[0] !== last[0]) {
    sampled.push(last);
  }
  return Object.fromEntries(sampled) as T;
};

const EquityCurve = ({
  height,
  selectedChartTypes,
  onSelectedChartTypesChange,
  showOutperformance = false,
  histories,
  transactionTypeToggleValues,
  onTransactionTypeToggleValuesChange,
  onLeftAxisWidthChange,
  onTimeAxisChange,
}: Props) => {
  const { raw, subscriptionView } = useResultData();
  const ref = useRef<HTMLDivElement | null>(null);
  const [dimensions, setDimensions] = useState<ComponentDimensions | null>(
    null,
  );

  const chartData = useMemo(() => {
    // The difference is taken BEFORE downsampling, upstream in ProfitChart.
    // Downsampling keeps every Nth entry per series independently, and the two
    // operands are different lengths — 316 balance points against 256 hold
    // points, since the balance series carries a point per transaction AND per
    // candle while the hold line is candle-only — so differencing a downsampled
    // pair would compare mismatched instants.
    const { stable, asset, nap } = histories ?? {
      stable: raw.endBalanceInUsdtHistory,
      asset: raw.endBalanceInAssetHistory,
      nap: raw.priceHistory,
    };

    return {
      ...raw,
      endBalanceInUsdtHistory: downsampleHistory(stable),
      endBalanceInAssetHistory: downsampleHistory(asset),
      priceHistory: downsampleHistory(nap),
    };
  }, [raw, histories]);

  // Outperformance is one line, so the series checkboxes cannot pick anything while it is on.
  // They stay live in the legend and keep their state, so switching it off restores exactly
  // what was selected before.
  const renderedChartTypes = useMemo(
    () => (showOutperformance ? [ChartType.StableBalance] : selectedChartTypes),
    [showOutperformance, selectedChartTypes],
  );

  useLayoutEffect(() => {
    if (!ref.current) return;
    const element = ref.current;
    const measure = () =>
      setDimensions({
        width: element.clientWidth,
        height: element.clientHeight,
      });
    measure();
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{ width: '100%', height, display: 'flex', position: 'relative' }}
    >
      {/* The TPT/MRC markers are drawn on the live subscription view only —
       * a backtest result has no notion of a feature go-live date. */}
      {dimensions && (
        <LegacyCharts
          data={chartData}
          allCompactTransactions={raw.compactTransactions ?? raw.transactions}
          rootDimensions={dimensions}
          setIsChartAnimationDone={noop}
          selectedChartTypesOverride={renderedChartTypes}
          onSelectedChartTypesChange={(next) =>
            onSelectedChartTypesChange(
              typeof next === 'function' ? next(selectedChartTypes) : next,
            )
          }
          isTransactionsSelectedOverride={true}
          transactionTypeToggleValuesOverride={transactionTypeToggleValues}
          onTransactionTypeToggleValuesChange={(next) =>
            onTransactionTypeToggleValuesChange(
              typeof next === 'function'
                ? next(transactionTypeToggleValues)
                : next,
            )
          }
          hideControls
          hideXAxis
          hideGrid
          showTimeGridlines
          showFeatureReferenceLines={!!subscriptionView}
          disableAnimation
          onLeftAxisWidthChange={onLeftAxisWidthChange}
          onTimeAxisChange={onTimeAxisChange}
        />
      )}
    </div>
  );
};

export default EquityCurve;
